<template>
  <div class="header-wrapper">
    <div class="branding">
      <span class="app-name">
        Navigo
      </span>
      <span><img src="../assets/header.png"/></span>
    </div>
    <SearchBar 
      ref="searchBar" 
      class="search-bar" 
      @search="updateSearch"
      :keyword="searchBarState.keyword"
      :place="searchBarState.place"
      :intersects="searchBarState.intersects"
      :expansion="searchBarState.expansion"
      />
    <div class="user-action-buttons">
      <div @click="userDropdown"
            class="user-action">
        <UserIcon
          stroke-width="1.5"
          size="36" />
        <span>
          {{ userFirstName }}
        </span>
      </div>
      <div @click="cartAction"
            class="user-action">
        <ShoppingCartIcon
          stroke-width="1.5"
          size="36" />
        <span class="cart-count">{{numberOfItemsFormatted}}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/store/auth';
import { useCartStore } from '@/store/cart';
import { useQueryStore } from '@/store/query';
import { SearchBar } from 'VoyagerComponents';
import { computed, reactive, ref, Ref, watch } from 'vue';
import { UserIcon, ShoppingCartIcon } from 'vue-tabler-icons';

const { getReadOnlyAuth: auth } = useAuthStore(),
  { getNumberOfItemsInCart } = useCartStore(),
  { 
    setQuery, 
    setPlace, 
    setIntersects, 
    setExpansion,
    getQuery,
    getPlace,
    getIntersects,
    getExpansion
  } = useQueryStore(),
  searchBar: Ref<typeof SearchBar | null> = ref(null),
  updateSearch = (searchbarState) => {
    const { keyword, place, intersects, expansion } = searchbarState;
    setQuery(keyword);
    setPlace(place);
    setIntersects(intersects);
    setExpansion(expansion);
    emit('updateSearch')
  },
  searchBarState = reactive({
    keyword: '',
    place: '',
    intersects: true,
    expansion: true
  }),
  emit = defineEmits(['showCart', 'showUser', 'updateSearch']),
  userDropdown = () => emit('showUser'),
  cartAction = () => emit('showCart'),
  userFirstName = computed(() => auth.username?.split(' ')[0] || ''),
  numberOfItemsFormatted = computed(() => {
    const numberOfItems = getNumberOfItemsInCart().value.toString();
    if(numberOfItems.length < 4) return numberOfItems;
    if(numberOfItems.length >= 4 && numberOfItems.length < 7) return `>${numberOfItems.substring(0, numberOfItems.length-3)}k` // 1234
    if(numberOfItems.length >= 7 && numberOfItems.length < 10) return `>${numberOfItems.substring(0, numberOfItems.length-6)}M` // 1234567
    return `>${numberOfItems.substring(0,numberOfItems.length-9)}B`
  });

  watch([getQuery, getPlace, getExpansion, getIntersects], () => {
    searchBarState.keyword = getQuery() || '';
    searchBarState.place = getPlace() || '';
    searchBarState.intersects = getIntersects() || false;
    searchBarState.expansion = getExpansion() || true;
  })

</script>

<style scoped lang="scss">
.header-wrapper {
  z-index: 450;
  flex-direction: row;
  width: 100%;
  font-family: 'Montserrat';
  font-variation-settings: 'wght' 500; 
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  justify-content: center;
  grid-template-areas: "branding search user";
  .branding {
    display: flex;
    flex-direction: column;
    padding-right: 0.5rem;
    grid-area: branding;
    width: fit-content;
    justify-self: flex-end;
    margin-left: 1rem;
    &:hover {
      cursor: default;
    }
    .app-name {
      font-size: 1.5rem;
      padding-bottom: 3px;
      border-bottom-style: solid;
    }
    img {
      height: 1.5rem;
      position: relative;
      top: 0.5rem;
    }
  }
  .user-action-buttons {
    grid-area: user;
    align-self: center;
    justify-self: flex-start;
    margin-left: 0.5rem;
    display: flex;
    border-radius: var(--border-radius);
    & > * {
      transition: all 0.15s ease;
      border-radius: var(--border-radius);
      padding: 0.2rem 0rem;
    }
    &>:hover {
      background-color: var(--color-background-light);
      cursor: pointer;
      padding-right: 0.5rem;
      padding-left: 0.5rem;
    }
    .user-action {
      display: flex; 
      flex-direction: row;
      align-items: center;
      border-radius: var(--border-radius);
    }
  }
  .search-bar {
    grid-area: search;
    width: clamp(300px, 600px, 80vw); 
    --color-action-background-primary: hsl(176deg 51% 57%);
  }
}

$breakpoint-tablet: 799px;
@media(max-width: $breakpoint-tablet) {
  .header-wrapper {
    grid-template-areas: "branding user"
                         "search search";
  }
}
</style>